import type { OpenImRawMessage } from './types';

/**
 * Mirrors Android `TgImOpenImMessageIdRegistry`: TG list/viewport order is by message `id`,
 * so ids must stay monotonic with real send time. Using raw OpenIM `seq` or a clientMsgID hash
 * alone interleaves history incorrectly (e.g. a 16:25 bubble after 22:50).
 *
 * Preferred id ≈ unixSec + (seq%999|+sendMs%999) + 1, cached per clientMsgID / seq key.
 */

const keyToId = new Map<string, number>();
const idToKey = new Map<number, string>();
let lastAssignedId = 0;

function openImMessageKey(raw: OpenImRawMessage): string {
  const clientMsgId = (raw.clientMsgID || '').trim();
  if (clientMsgId) return clientMsgId;
  const serverMsgId = String((raw as { serverMsgID?: unknown }).serverMsgID || '').trim();
  if (serverMsgId) return `server:${serverMsgId}`;
  const seq = Number(raw.seq) || Number((raw as { clientMsgSeq?: unknown }).clientMsgSeq) || 0;
  if (seq > 0) return `seq:${seq}`;
  return `syn:${raw.sendID || ''}:${raw.sendTime || 0}`;
}

function toUnixMillis(ts: number): number {
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  // OpenIM commonly uses ms; values < 1e12 are treated as seconds.
  return ts < 1e12 ? Math.floor(ts * 1000) : Math.floor(ts);
}

function computeTimeBasedId(raw: OpenImRawMessage, dateSec: number): number {
  let seq = Number(raw.seq) || 0;
  if (seq <= 0) {
    seq = Number((raw as { clientMsgSeq?: unknown }).clientMsgSeq) || 0;
  }
  let sendMs = toUnixMillis(Number(raw.sendTime) || 0);
  if (sendMs <= 0 && dateSec > 0) {
    sendMs = dateSec * 1000;
  }
  const sec = sendMs > 0
    ? Math.min(Number.MAX_SAFE_INTEGER - 10_000, Math.floor(sendMs / 1000))
    : Math.max(1, dateSec);
  const tie = seq > 0
    ? (seq % 999) + 1
    : (sendMs % 999) + 1;
  // Keep within signed 32-bit-ish range Telegram-style UIs expect for mid sorting.
  return Math.min(2_100_000_000, sec + tie);
}

function bumpToMonotonic(id: number): number {
  let next = id;
  if (next <= lastAssignedId) {
    next = lastAssignedId + 1;
  }
  lastAssignedId = next;
  return next;
}

export function assignOpenImMessageId(raw: OpenImRawMessage): number {
  const key = openImMessageKey(raw);
  const cached = keyToId.get(key);
  if (cached) {
    idToKey.set(cached, key);
    lastAssignedId = Math.max(lastAssignedId, cached);
    return cached;
  }
  const dateSec = Math.floor(toUnixMillis(Number(raw.sendTime) || 0) / 1000)
    || Math.floor(Date.now() / 1000);
  const id = bumpToMonotonic(computeTimeBasedId(raw, dateSec));
  if (key) {
    keyToId.set(key, id);
    idToKey.set(id, key);
  }
  return id;
}

/** Keep optimistic/local id after send ack (Android `adoptServerIdForPending`). */
export function bindOpenImMessageId(clientMsgId: string, messageId: number): void {
  if (!clientMsgId || messageId <= 0) return;
  const prev = keyToId.get(clientMsgId);
  if (prev !== undefined && prev !== messageId) {
    idToKey.delete(prev);
  }
  keyToId.set(clientMsgId, messageId);
  idToKey.set(messageId, clientMsgId);
  lastAssignedId = Math.max(lastAssignedId, messageId);
}

/** Resolve a previously assigned mid from OpenIM clientMsgID (quote/forward reply targets). */
export function getOpenImMessageIdByClientMsgId(clientMsgId: string): number | undefined {
  if (!clientMsgId) return undefined;
  return keyToId.get(clientMsgId);
}

/** Reverse of `assignOpenImMessageId` — only works for ids still in this session registry. */
export function getOpenImClientMsgIdByMessageId(messageId: number): string | undefined {
  if (!messageId) return undefined;
  const key = idToKey.get(messageId);
  if (!key || key.startsWith('server:') || key.startsWith('seq:') || key.startsWith('syn:')) {
    return undefined;
  }
  return key;
}

/** Test helper. */
export function resetOpenImMessageIdRegistry(): void {
  keyToId.clear();
  idToKey.clear();
  lastAssignedId = 0;
}
